import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { forEach as _forEach, has as _has } from 'lodash';
import { combineLatest, filter, map, Observable, Subject, takeUntil } from 'rxjs';

import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import { AutoPayUtilService } from '@amfam/billing/auto-pay/feature';
import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { BillAccount } from '@amfam/shared/models';
import { CopyService } from '@amfam/shared/utility/shared-services';

@Component({
  selector: 'ds-manage-autopay',
  templateUrl: './manage-autopay.component.html',
  styleUrls: ['./manage-autopay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ManageAutopayComponent implements OnInit, OnDestroy {
  autoPayAccounts = [];
  autopayEnrolledAccount = [];
  autopayNotEnrolledAccount = [];
  dataLoading = true;
  autopayAccountsList = { notEnrolledAccounts: [], enrolledAccounts: [] };
  public dataLoading$: Observable<boolean>;

  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private copyService: CopyService,
    private store: Store,
    private autoPayUtil: AutoPayUtilService
  ) {}

  ngOnInit() {
    this.dataLoading$ = this.store.select(BillAccountsSelectors.selectBillAccountsLoading);

    combineLatest([
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      this.store.select(AutoPaySelectors.getAutomaticPayments)
    ])
      .pipe(
        map(([billAccounts, automaticPayments]) => ({
          billAccounts,
          automaticPayments
        })),
        filter(state => state.billAccounts?.length > 0),
        takeUntil(this.stop$)
      )
      .subscribe(state => {
        if (state.automaticPayments) {
          this.autoPayAccounts = state.automaticPayments;
        }

        this.autopayEnrolledAccount = [];
        this.autopayNotEnrolledAccount = [];
        _forEach(state.billAccounts, (billAccount: BillAccount) => {
          if (
            _has(billAccount, 'billAccountNumber') &&
            (_has(billAccount, 'policyList') || _has(billAccount, 'policyTypeDisplayName'))
          ) {
            this.setAutoPayList(billAccount);
          }
        });
        if (this.autopayNotEnrolledAccount.length) {
          this.autopayAccountsList.notEnrolledAccounts = this.autopayNotEnrolledAccount;
        }

        if (this.autopayEnrolledAccount.length) {
          this.autopayAccountsList.enrolledAccounts = this.autopayEnrolledAccount;
        }
      });
  }

  setAutoPayList(billAccount) {
    const billAcc = {
      isAutoPayEnabled: this.isAutoPayEnabled(billAccount),
      accountNumber: billAccount.billAccountNumber,
      riskDesc: this.getRiskDesc(billAccount)
    };
    if (billAcc.isAutoPayEnabled) {
      this.autopayEnrolledAccount.push(billAcc);
    } else {
      this.autopayNotEnrolledAccount.push(billAcc);
    }
  }

  isAutoPayEnabled(billAccount) {
    const billAccountHasAutoPay: boolean = this.autoPayAccounts
      .map(autoPay => autoPay?.billAccountNumber)
      .includes(billAccount.billAccountNumber);
    return billAccountHasAutoPay;
  }

  getRiskDesc(billAccount) {
    const risk = [];
    if (billAccount.policyList?.length && billAccount.policyList[0].riskDescriptionList) {
      risk.push(billAccount.policyList[0].riskDescriptionList);
    } else if (billAccount.policyTypeDisplayName) {
      risk.push(billAccount.policyTypeDisplayName);
    }
    return risk;
  }

  setUpAutopay(billAccountNumber: string) {
    this.autoPayUtil.routeToMultipleAutoPaySelection(`/billing/${billAccountNumber}`, [
      billAccountNumber
    ]);
  }

  editAutoPayClick(billAccountNumber: string) {
    // AUTOPAY ENTRY POINT
    this.autoPayUtil.editAutoPay(billAccountNumber, `/billing/${billAccountNumber}`);
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
